import React from 'react';
import { Input, Select } from 'antd';
import type { SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { CustomButton, CustomTitle } from '../../../components';
import './BulkUploadEditor.css';

const SENSOR_SLOT_PROVIDER_NAME_RE = /^sensor_slot_\d+_name$/;

type ProductSelectOptionRenderOption = Parameters<
  NonNullable<SelectProps<unknown, DefaultOptionType>['optionRender']>
>[0];

function renderProductSelectOption(oriOption: ProductSelectOptionRenderOption) {
  const data = oriOption.data;
  const raw = data?.label ?? oriOption.label ?? oriOption.value;
  const text =
    typeof raw === 'string' || typeof raw === 'number'
      ? String(raw)
      : String(oriOption.value ?? '');
  return (
    <span className='bulk-upload-product-option-label' title={text}>
      {text}
    </span>
  );
}

const PRODUCT_SELECT_DROPDOWN_PROPS = {
  virtual: false as const,
  popupMatchSelectWidth: false as const,
  getPopupContainer: () => document.body,
  classNames: { popup: { root: 'bulk-upload-product-select-dropdown' } },
  styles: {
    popup: {
      root: {
        minWidth: 280,
        maxWidth: 520,
        zIndex: 10050,
      },
    },
  },
  optionRender: renderProductSelectOption,
};

function isSensorSlotProviderNameKey(key: string): boolean {
  return typeof key === 'string' && SENSOR_SLOT_PROVIDER_NAME_RE.test(key);
}

interface Courier {
  courier_id: string;
  courier_name: string;
}

interface Sensor {
  sensor_id?: string;
  sensor_name: string;
}

interface Product {
  id: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
}

interface Location {
  sensor_name?: string;
  location_name?: string;
  name?: string;
}

export interface BulkFieldSelectorProps {
  editingFieldKey: string;
  bulkFieldValue: string | undefined;
  setBulkFieldValue: (value: string | undefined) => void;
  couriersList: Courier[];
  sensorsList: Sensor[];
  productList: Product[];
  allTeams: Team[];
  locationList: Location[];
  setEditModalOpen: (open: boolean) => void;
  handleSave: () => void;
}

/**
 * BulkFieldSelector - Dynamic field editor for bulk shipment operations
 *
 * Provides a dynamic form field interface that adapts input type and validation
 * based on the field being edited. It supports both select dropdowns
 * for predefined options and text inputs with custom validation rules.
 *
 * The component handles various shipment field types:
 * - Dropdown selects: courier, sensor, sensor_slot_N_name (same options as sensor),
 *   products (multi), teams (multi), locations
 * - Text inputs: description, link_code (with alphanumeric validation), and other text fields
 *
 * @param editingFieldKey - The key identifying which field is being edited
 * @param bulkFieldValue - Current value of the field being edited
 * @param setBulkFieldValue - Function to update the field value
 * @param couriersList - Available courier options
 * @param sensorsList - Available sensor options
 * @param productList - Available product options
 * @param allTeams - Available team options
 * @param locationList - Available location options
 * @param setEditModalOpen - Function to control modal visibility
 * @param handleSave - Function to save the current field changes
 */
const BulkFieldSelector: React.FC<BulkFieldSelectorProps> = ({
  editingFieldKey,
  bulkFieldValue,
  setBulkFieldValue,
  couriersList,
  sensorsList,
  productList,
  allTeams,
  locationList,
  setEditModalOpen,
  handleSave,
}) => {
  const renderSelectOptions = (): { label: string; value: string }[] => {
    switch (editingFieldKey) {
      case 'courier':
        return couriersList.map((c) => ({
          label: c.courier_name,
          value: c.courier_id,
        }));
      case 'sensor':
        return sensorsList.map((s) => ({
          label: s.sensor_name,
          value: String(s.sensor_id ?? s.sensor_name),
        }));
      case 'products':
        return productList.map((p) => ({
          label: p.name,
          value: String(p.id),
        }));

      case 'teams':
        return allTeams.map((t) => ({
          label: t.name,
          value: String(t.id),
        }));
      case 'from_location':
      case 'to_location':
        return locationList.map((l) => ({
          label: l.sensor_name || l.location_name || l.name,
          value: l.sensor_name || l.location_name || l.name,
        }));
      default:
        if (isSensorSlotProviderNameKey(editingFieldKey)) {
          return sensorsList.map((s) => ({
            label: s.sensor_name,
            value: String(s.sensor_id ?? s.sensor_name),
          }));
        }
        return [];
    }
  };

  const isMulti = ['teams', 'products'].includes(editingFieldKey);
  const showSelect =
    [
      'courier',
      'sensor',
      'teams',
      'products',
      'from_location',
      'to_location',
    ].includes(editingFieldKey) || isSensorSlotProviderNameKey(editingFieldKey);

  return (
    <>
      <CustomTitle
        text='This will apply to all shipments (this can be changed for individual shipments later)'
        fontSize='14px'
        color='var(--text-secondary)'
        fontWeight='400'
        marginBottom='4px'
      />

      {showSelect ? (
        <Select
          style={{
            width: '100%',
            border: 'none',
            backgroundColor: 'var(--light-gray)',
            borderRadius: 6,
            padding: '4px 8px',
            marginTop: 12,
            height: 'auto',
          }}
          className='gray-tag-select'
          mode={isMulti ? 'multiple' : undefined}
          value={bulkFieldValue}
          placeholder='Select...'
          labelInValue
          onChange={setBulkFieldValue}
          options={renderSelectOptions()}
          {...(editingFieldKey === 'products'
            ? PRODUCT_SELECT_DROPDOWN_PROPS
            : {})}
        />
      ) : (
        <Input
          style={{
            width: '100%',
            height: 44,
            fontSize: 12,
            backgroundColor: 'var(--light-gray)',
            border: 'none',
            borderRadius: 6,
            paddingLeft: 8,
            marginTop: 12,
          }}
          value={bulkFieldValue}
          maxLength={editingFieldKey === 'link_code' ? 255 : undefined}
          onChange={(e) => {
            const input = e.target.value;
            if (editingFieldKey === 'link_code') {
              const isValid = /^[a-zA-Z0-9]*$/.test(input);
              if (isValid) setBulkFieldValue(input);
            } else {
              setBulkFieldValue(input);
            }
          }}
        />
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
        }}
      >
        <CustomButton
          key='cancel'
          text='Cancel'
          onClick={() => setEditModalOpen(false)}
          bgColor='transparent'
          color='var(--text-primary)'
          border='none'
          boxShadow='none'
          fontSize='14px'
          fontWeight='500'
          height='36px'
          padding='15px 15px 15px 0px'
          borderRadius='8px'
        />

        <CustomButton
          key='save'
          text='Save'
          bgColor='#000000'
          color='#FFFFFF'
          fontSize='14px'
          width='60px'
          borderRadius='8px'
          fontWeight='600'
          height='36px'
          onClick={handleSave}
        />
      </div>
    </>
  );
};

export default BulkFieldSelector;
